import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Icon, Modal } from 'components';
import './RatingModal.scss';

const getRatingLabel = (value) => {
  if (value <= 0) return '';
  if (value <= 1) return 'Very poor';
  if (value <= 2) return 'Poor';
  if (value <= 3) return 'Average';
  if (value <= 4) return 'Good';
  return 'Excellent';
};

export const RatingModal = ({
  isOpen,
  projectName = '',
  initialRating = 0,
  initialReview = '',
  onClose,
  onSubmit = null,
}) => {
  const [rating, setRating] = useState(initialRating || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState(initialReview || '');

  const effectiveRating = hoverRating > 0 ? hoverRating : rating;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (onSubmit) {
      onSubmit({ rating, review });
    }

    onClose();
  };

  const handleClose = () => {
    setHoverRating(0);
    onClose();
  };

  const getStarValueFromEvent = (event, baseValue) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const relativeX = event.clientX - rect.left;
    if (relativeX < rect.width / 2) {
      return baseValue - 0.5;
    }
    return baseValue;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title='Your opinion matters to us!'
      showFooter={false}
      width='420px'
    >
      <form className='rating-modal' onSubmit={handleSubmit}>
        <div className='rating-modal-header'>
          <p className='rating-modal-question'>
            How was the quality of this project?
          </p>
          {projectName && <p className='rating-modal-project'>{projectName}</p>}
        </div>

        <div
          className='rating-modal-stars'
          onMouseLeave={() => setHoverRating(0)}
        >
          {[1, 2, 3, 4, 5].map((value) => {
            const diff = effectiveRating - value;
            let starClass = 'empty';
            if (diff >= 0) {
              starClass = 'filled';
            } else if (diff >= -0.5) {
              starClass = 'half';
            }

            const isActive = effectiveRating >= value - 0.5;

            return (
              <button
                key={value}
                type='button'
                className={`rating-modal-star-button ${
                  isActive ? 'active' : ''
                }`}
                onClick={(e) => setRating(getStarValueFromEvent(e, value))}
                onMouseMove={(e) =>
                  setHoverRating(getStarValueFromEvent(e, value))
                }
              >
                <Icon
                  name='Star'
                  className={`rating-modal-star ${starClass}`}
                />
              </button>
            );
          })}
        </div>

        <div className='rating-modal-label'>
          {effectiveRating > 0 && (
            <>
              <span className='rating-modal-label-text'>
                {getRatingLabel(effectiveRating)}
              </span>
              <span className='rating-modal-label-value'>
                {`${effectiveRating.toFixed(1)} / 5`}
              </span>
            </>
          )}
        </div>

        <div className='rating-modal-input'>
          <textarea
            rows={3}
            value={review}
            onChange={(e) => setReview(e.target.value)}
            placeholder='Leave a message, if you want'
          />
        </div>

        <div className='rating-modal-actions'>
          <button type='submit' className='rating-modal-primary'>
            Rate now
          </button>
          <button
            type='button'
            className='rating-modal-secondary'
            onClick={handleClose}
          >
            Maybe later
          </button>
        </div>
      </form>
    </Modal>
  );
};

RatingModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  projectName: PropTypes.string,
  initialRating: PropTypes.number,
  initialReview: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
};
